import { useQuery } from '@tanstack/react-query'
import { FC, useEffect } from 'react'

import { useErrorHandler } from '../../../hooks/useErrorHandler'
import { Dashboard } from '../../../models/Dashboard'
import { RecordFactory } from '../../../models/RecordFactory'
import { RecordNotFoundError } from '../../../models/errors/RecordNotFoundError'
import {
    ADDITIONAL_FEEDBACK_STEP,
    NPSStepAdditionalFeedbackPage,
} from './NPSStepAdditionalFeedbackPage'
import { NPSStepBasePage } from './NPSStepBasePage'
import { CONFIRM_COMPLETE_STEP, NPSStepConfirmCompletePage } from './NPSStepConfirmCompletePage'
import { CONFIRM_CONTINUE_STEP, NPSStepConfirmContinuePage } from './NPSStepConfirmContinuePage'
import { FEATURE_FEEDBACK_STEP, NpsStepFeatureFeedback } from './NPSStepFeatureFeedbackPage'
import { NPSStepRationalePage, RATIONALE_STEP } from './NPSStepRationalePage'
import {
    NPSStepSatisfactionRatingPage,
    SATISFACTION_RATINGS_STEP,
} from './NPSStepSatisfactionRatingPage'
import { NPSStepScorePage, SCORE_STEP } from './NPSStepScorePage'
import { FieldIds, NPS_FORM, NPSStepId, NPSStepProps, StepIds } from './types'
import { NPSRecordState } from './NPSViewModel'
import {
    NPSWorkflowStep,
    useNPSFormWorkflowCoordinator,
} from './useNPSFormWorkflowCoordinator'

const STEPS = [
    {
        spec: SCORE_STEP,
        component: NPSStepScorePage,
    },
    {
        spec: RATIONALE_STEP,
        component: NPSStepRationalePage,
    },
    {
        spec: CONFIRM_CONTINUE_STEP,
        component: NPSStepConfirmContinuePage,
    },
    {
        spec: FEATURE_FEEDBACK_STEP,
        component: NpsStepFeatureFeedback,
    },
    {
        spec: SATISFACTION_RATINGS_STEP,
        component: NPSStepSatisfactionRatingPage,
    },
    {
        spec: ADDITIONAL_FEEDBACK_STEP,
        component: NPSStepAdditionalFeedbackPage,
    },
    {
        spec: CONFIRM_COMPLETE_STEP,
        component: NPSStepConfirmCompletePage,
    },
] as const satisfies {
    spec: NPSWorkflowStep<NPSStepId>
    component: FC<NPSStepProps>
}[]

const STEP_SPECS = STEPS.map(({ spec }) => spec)
const STEP_COMPONENTS = STEPS.map(({ component }) => component)
const FIELDS = Object.values(FieldIds)
/**
 * Last step in which we accept user input, ie. before the "done" step.
 */
const LAST_INPUT_STEP_SPEC = STEP_SPECS[STEP_SPECS.length - 2]

async function loadNpsData() {
    const [markDashboardResult, dashboard, form, fields] = await Promise.all([
        // We assume the dashboard has already been fetched by the home page, here we are only fetching to
        // mark the dashboard as viewed. It is still awaited, though, because currently if the dashboard is
        // not active for the user this call will yield error.
        RecordFactory.getInstance().fetchDashboard('alerts_nps', undefined, undefined, 0, 'all'),
        RecordFactory.getInstance().loadDashboard('alerts_nps'),
        RecordFactory.getInstance().loadType(NPS_FORM),
        RecordFactory.getInstance().loadRecordFields(NPS_FORM, false),
    ])

    if (!dashboard || !form || !fields) {
        throw new RecordNotFoundError('Could not load NPS form data.')
    }

    return { markDashboardResult, dashboard, form, fields }
}

function shouldConfirmClose(
    { id }: NPSWorkflowStep<NPSStepId>,
    { getFieldValue }: NPSRecordState
) {
    if (
        // Don't show confirmation if we're on the first step and have no input.
        (id === StepIds.SCORE && getFieldValue(FieldIds.SCORE) === undefined) ||
        // Neither if we are on the last step.
        id === StepIds.CONFIRM_COMPLETE
    ) {
        return false
    }
    return true
}

/**
 * Marks the user's NPS dashboard setting as 'dismissed'
 *
 * Note that we use the same setting state for complete and dismissed. The intent is to
 * denote user interaction, not store a completed state on the dashboard.
 */
async function markDismissed(dashboard: Dashboard) {
    await RecordFactory.getInstance().markDashboard(dashboard, 'dismissed', undefined)
}

type NpsWorkflowCoordinatorWithDataProps = {
    data: Awaited<ReturnType<typeof loadNpsData>>
}

function NpsWorkflowCoordinatorWithData({ data }: NpsWorkflowCoordinatorWithDataProps) {
    const { dashboard } = data

    const workflow = useNPSFormWorkflowCoordinator({
        data,
        steps: STEP_SPECS,
        fields: FIELDS,
        initialStep: StepIds.SCORE,
        shouldSaveOnNext: true,
        shouldConfirmClose,
        onClose: async () => {
            try {
                await markDismissed(dashboard)
            }
            catch (err) {
                console.error('Error in onClose NPS survey workflow', err)
            }

            // We use window location in this and the following kickbacks in order to ensure both that
            // the home dashboards are reloaded as well as to ensure the user cannot go "back" without
            // reloading the page and thus circumventing guards against resubmitting the survey.
            //
            // TODO Instead, use react-query invalidation and somehow prevent back navigation.
            window.location.href = '/'
        },
        onComplete: async () => {
            window.location.href = '/'
        },
        onNext: async (currentStepId: NPSStepId) => {
            // Ensure dismissed is marked at exactly the time the form is completed so that
            // the banner no longer shows.
            if (currentStepId === LAST_INPUT_STEP_SPEC.id) {
                try {
                    await markDismissed(dashboard)
                }
                catch (err) {
                    console.error('Error in onNext NPS survey workflow', err)
                }
            }
        },
    })

    const Step = STEP_COMPONENTS[workflow.currentStepIndex]

    return (
        <NPSStepBasePage title="Product Feedback" workflow={workflow}>
            <Step {...workflow} />
        </NPSStepBasePage>
    )
}

/**
 * Multistep form collecting NPS and related data from users.
 * Assumes relevant dashboard, type, and fields have already been fetched and stored locally.
 */
export function NPSWorkflowCoordinator() {
    const { logAndDisplayError } = useErrorHandler()
    const { data } = useQuery({
        queryKey: ['nps'],
        queryFn: async () => {
            try {
                return await loadNpsData()
            }
            catch (err) {
                logAndDisplayError(err)
                throw err
            }
        },
    })

    useEffect(() => {
        // This indicates the dashboard is not active for the user, kick them back
        // to the home page. This would happen if e.g., the user revisits the URL
        // directly after completing the survey.
        const ok = data?.markDashboardResult?.ok
        if (typeof ok === 'boolean' && !ok) {
            window.location.href = '/'
        }
    }, [data])

    if (!data || !data.markDashboardResult.ok) {
        return null
    }

    return <NpsWorkflowCoordinatorWithData data={data} />
}
